/**
 * MER-497: pure decision for a widget's EFFECTIVE period grain and
 * default current-period window under the global filters.
 *
 * Why this exists: the global Period selector defaults to "monthly" and
 * applyGlobalFilters used to clobber every DECLARED `filter.period_type`
 * with that default. A tile declaring weekly grain + a current-period
 * range therefore queried monthly rows through a 7-day window on first
 * load and rendered n/a (11 dead tiles across /dws + /bestdisposal,
 * 2026-06-10 punch-list re-baseline). The rule now is:
 *
 *   - A DECLARED period_type survives the default load. Only an EXPLICIT
 *     user selection (URL carries a valid `period` param) overrides it.
 *     This also stops the default from clobbering snapshot-declared
 *     widgets (the MER-155 failure mode, previously only avoidable via
 *     suppress_dashboard_filter).
 *   - Auto-injection for manifests that declare nothing stays exactly as
 *     MER-362 shipped it: period-aware sources in period mode receive the
 *     global value; event-grain sources stay untouched.
 *   - The current-period range rewrite keys off the EFFECTIVE grain:
 *     monthly resolves to last_month (the MER-421/MER-304 complete-month
 *     rule, unchanged); weekly resolves to last_30_days. Weekly does NOT
 *     use a last-complete-week window because kpi_summary.period_start is
 *     TEXT with mixed grains (MER-469) and the now-cutoff
 *     (`period_start <= "YYYY-MM"`) lexicographically excludes every
 *     current-month weekly row, so a one-week window can return zero rows
 *     for most of the month. last_30_days spans the most recent complete
 *     weeks that pass the cutoff (the shape MER-465 PR B verified at
 *     99.9%), and tightens naturally once MER-469 lands. The rewrite only
 *     applies when the widget DECLARED a period_type; silent widgets keep
 *     their literal calendar windows (this_week / this_month), matching
 *     the pre-existing "do NOT default a missing period_type to monthly"
 *     rule.
 *
 * MER-636 UPDATE. Two claims in the paragraph above have moved and the module
 * is left as it is on purpose.
 *
 *   - The stated reason weekly resolves to last_30_days rather than a
 *     one-week window ("kpi_summary.period_start is TEXT with mixed grains,
 *     so the now-cutoff lexicographically excludes every current-month weekly
 *     row") stopped being true at MER-500, which moved the cutoff onto the
 *     real DATE column period_date. The DEFAULT is unchanged anyway: a
 *     trailing 30 days is a reasonable weekly default on its own merits, and
 *     narrowing it is a product decision, not a bug fix.
 *   - "Authored trailing windows keep their declared width" still holds.
 *     MER-636 changed which period BUCKETS a width selects (queries.ts ->
 *     periodWindow.mjs), never the width itself. last_90_days still asks for
 *     90 days; it now answers with three whole months instead of two plus a
 *     partial.
 *
 * MER-692 EXCEPTION, deliberate. An explicit calendar month (`?range=YYYY-MM`,
 * which arrives here as rangeExplicit with globalManifestRange
 * `month:YYYY-MM`) with Period left on Default resolves every widget the month
 * overrides on a period-aware source to MONTHLY grain, declared weekly
 * included, exactly as if Monthly had been selected. That is the one place a
 * declared grain loses on a load with no explicit period. The reason: a month
 * is a calendar month. Left weekly, a count tile answers with the weeks the
 * month mostly covers (July 2026 as 29 Jun to 2 Aug, 35 days) under the same
 * month label as the monthly tile beside it, which covers 1 to 31 Jul. The
 * exception is narrow on purpose:
 *
 *   - Only widgets whose window the month replaces (period mode, declared
 *     range in the current-period set). Trailing trend charts keep both their
 *     window and their declared grain.
 *   - Only period-aware sources. Event-grain sources carry no period_type and
 *     keep exact calendar-month bounds on their date column.
 *   - An EXPLICIT Period still wins. Weekly or Daily plus a month keeps that
 *     grain, and the tile says so ("Weeks of Jul 2026", globalFilters.ts
 *     widgetRangeLabel).
 *   - A preset range (MTD, L30, ...) does not trigger it. MER-497 is unchanged
 *     there.
 *
 * Pure so node:test imports it directly with no TypeScript build step (the
 * tenantScope.mjs / fallbackWindow.mjs precedent); its one import,
 * calendarMonth.mjs, is pure too. The companion grainDefaults.d.mts gives TS
 * callers types.
 */

import { parseMonthToken } from "./calendarMonth.mjs";

/**
 * Calendar-period ranges eligible for the weekly default-window rewrite.
 * Trailing windows (last_30_days / last_90_days) are an AUTHORED width;
 * squashing a declared last_90_days to 30 days would silently change a
 * future widget's meaning, so the weekly branch only rewrites the
 * calendar shapes that are provably dead under the MER-469 cutoff.
 */
const WEEKLY_CALENDAR_RANGES = new Set(["this_week", "last_week", "this_month", "last_month"]);

/**
 * @param {{
 *   declaredPeriodType: string | undefined,
 *   timelineIsPeriod: boolean,
 *   sourceIsPeriodAware: boolean,
 *   globalPeriod: string,
 *   periodExplicit: boolean,
 *   declaredRange: string | undefined,
 *   declaredRangeIsCurrentPeriod: boolean,
 *   rangeExplicit: boolean,
 *   globalManifestRange: string,
 * }} input
 * @returns {{ periodType: string | undefined, range: string | undefined }}
 *   `periodType` undefined = leave the widget's filter untouched;
 *   `range` undefined = leave the widget's timeline.range untouched.
 */
export function resolveGrainAndRange(input) {
  const {
    declaredPeriodType,
    timelineIsPeriod,
    sourceIsPeriodAware,
    globalPeriod,
    periodExplicit,
    declaredRange,
    declaredRangeIsCurrentPeriod,
    rangeExplicit,
    globalManifestRange,
  } = input;

  const declares = declaredPeriodType !== undefined;

  // MER-692: a calendar month replaces this widget's window with Period on
  // Default. See the MER-692 EXCEPTION in the module header.
  const followsRange = timelineIsPeriod && declaredRangeIsCurrentPeriod;
  const monthOnDefaultPeriod =
    !periodExplicit && rangeExplicit && parseMonthToken(globalManifestRange) !== null;

  // Effective grain.
  let periodType;
  if (monthOnDefaultPeriod && followsRange && sourceIsPeriodAware) {
    // MER-692: exactly as if Monthly were selected, declared weekly included.
    periodType = "monthly";
  } else if (declares) {
    // MER-497: declared grain survives the DEFAULT; explicit selection wins.
    periodType = periodExplicit ? globalPeriod : declaredPeriodType;
  } else if (timelineIsPeriod && sourceIsPeriodAware) {
    // MER-362 auto-apply, unchanged.
    periodType = globalPeriod;
  } else {
    periodType = undefined;
  }

  // Default window for current-period widgets in period mode.
  let range;
  if (timelineIsPeriod && declaredRangeIsCurrentPeriod) {
    if (rangeExplicit) {
      // Explicit user selection wins for every overridable widget (MER-421).
      range = globalManifestRange;
    } else if (declares && periodType === "monthly") {
      // Last COMPLETE calendar month, never the in-progress partial month.
      range = "last_month";
    } else if (
      declares &&
      periodType === "weekly" &&
      declaredRange !== undefined &&
      WEEKLY_CALENDAR_RANGES.has(declaredRange)
    ) {
      // MER-497: trailing 30 days = the most recent complete weeks that
      // pass the MER-469 now-cutoff. See module header. Authored trailing
      // windows (last_30_days / last_90_days) keep their declared width.
      range = "last_30_days";
    }
  }

  return { periodType, range };
}
